import React from 'react';
import MultiPage from './MultiPage';
import EditableText from './EditableText';

const SECTION_KEYS = {
  'comments-header--self': 'self',
  'comments-header--manager': 'manager',
  'comments-header--team': 'team',
  'comments-header--peer': 'peer',
};

function SectionHeader({ colorClass, marginTop, configKey, sectionKey }) {
  return (
    <div
      className={`comments-section__header ${colorClass}`}
      style={marginTop ? { marginTop: 16 } : undefined}
    >
      <EditableText
        as="h4"
        path={`${configKey}.sectionHeaders.${sectionKey}`}
        variant="inline"
        stripInlineWrap
      />
    </div>
  );
}

/**
 * Builds a flat array of renderable items (section headers + individual
 * comment rows) so MultiPage can split at the exact comment boundary
 * where the page runs out of space.
 */
function buildItems(data, configKey) {
  const items = [];
  let hasPrevSection = false;

  function pushSection(comments, colorClass) {
    if (!comments || comments.length === 0) return;
    const sectionKey = SECTION_KEYS[colorClass];
    items.push(
      <SectionHeader
        key={`${colorClass}-header`}
        colorClass={colorClass}
        marginTop={hasPrevSection}
        configKey={configKey}
        sectionKey={sectionKey}
      />
    );
    comments.forEach((comment, idx) => {
      items.push(
        <div key={`${colorClass}-${idx}`} className="comments-section__item">
          <p>{typeof comment === 'string' ? comment.trim() : comment}</p>
        </div>
      );
    });
    hasPrevSection = true;
  }

  // Self
  pushSection(data.selfComments, 'comments-header--self');

  // Manager(s) – each manager gets its own header block
  if (data.managerComments && data.managerComments.length > 0) {
    data.managerComments.forEach((manager, mIdx) => {
      items.push(
        <SectionHeader
          key={`mgr-header-${mIdx}`}
          colorClass="comments-header--manager"
          marginTop={hasPrevSection}
          configKey={configKey}
          sectionKey="manager"
        />
      );
      manager.comments.forEach((comment, cIdx) => {
        items.push(
          <div key={`mgr-${mIdx}-${cIdx}`} className="comments-section__item">
            <p>{comment.trim()}</p>
          </div>
        );
      });
      hasPrevSection = true;
    });
  }

  // Team members
  pushSection(data.teamMemberComments, 'comments-header--team');

  // Peers
  pushSection(data.peerComments, 'comments-header--peer');

  return items;
}

export default function CommentsPage({ data, personName, sectionNumber, title, pageNumber, configKey }) {
  const header = (
    <>
      <h2 className="pdf-section-heading">
        <span className="pdf-section-heading__badge">{sectionNumber}</span>
        <EditableText
          as="span"
          path={`${configKey}.sectionHeading`}
          variant="inline"
          stripInlineWrap
          fallback={title}
        />
      </h2>
      <EditableText
        as="div"
        className="pdf-body-text"
        path={`${configKey}.pageConstants`}
        variant="block"
      />
    </>
  );

  return (
    <MultiPage
      header={header}
      items={buildItems(data, configKey)}
      startPageNumber={pageNumber}
      personName={personName}
      sliceKey={configKey}
      sectionKey={configKey}
    />
  );
}
